import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Box, FormHelperText, FormLabel } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import type { ErrorSchema } from '@rjsf/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { templatizeStepFunctionDefinition } from 'config-editor-common';
/* eslint-disable-next-line -- reusing the editor chrome from config-editor (intentional cross-plugin use) */
import {
  JsonMonacoEditor,
  TemplatizeSuggestionAlert,
  ValidationErrorsAlert,
  useTemplatizeToggle,
} from 'config-editor';

type UiOptions = {
  rows?: number;
  /**
   * Field name in the scaffolder formData that contains a JSON object of additional template vars.
   * Defaults to "definition_template_vars".
   */
  varsFieldName?: string;
  /**
   * Optional nested path inside varsFieldName (e.g. "definition_template_vars").
   */
  varsFieldPath?: string;
  /**
   * Known placeholders that are available without the user providing them (e.g. derived/constants).
   */
  knownPlaceholders?: string[];
  /**
   * If true, block submission when hardcoded env-specific values are detected that should be placeholderized.
   * Defaults to true.
   */
  enforceTemplatizedDefinition?: boolean;
};

type Props = FieldExtensionComponentProps<string, UiOptions>;

function stableErrorKey(errors: string[]) {
  return errors.slice().sort().join('\n');
}

function extractPlaceholderTokens(input: string) {
  const all = Array.from(input.matchAll(/\$\{([^}]+)\}/g))
    .map(m => m[1])
    .filter(Boolean);
  const valid = Array.from(input.matchAll(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g))
    .map(m => m[1])
    .filter(Boolean);
  const invalid = all.filter(t => !/^[A-Za-z_][A-Za-z0-9_]*$/.test(t));
  return { all, valid, invalid };
}

function tryParseJsonObject(value: unknown) {
  if (value === undefined || value === null) return { obj: {}, error: null as string | null };
  if (typeof value === 'object' && !Array.isArray(value)) {
    return { obj: value as Record<string, unknown>, error: null as string | null };
  }
  if (typeof value !== 'string') {
    return { obj: {}, error: 'must be a JSON object (e.g. {"key":"value"})' };
  }
  if (!value) return { obj: {}, error: null as string | null };
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { obj: {}, error: 'must be a JSON object (e.g. {"key":"value"})' };
    }
    return { obj: parsed as Record<string, unknown>, error: null };
  } catch (e) {
    return { obj: {}, error: e instanceof Error ? e.message : String(e) };
  }
}

function getByPath(input: unknown, path: string | undefined): unknown {
  if (!path) return input;
  if (!input || typeof input !== 'object') return undefined;
  const parts = path.split('.').filter(Boolean);
  let curr: any = input;
  for (const p of parts) {
    if (!curr || typeof curr !== 'object') return undefined;
    curr = curr[p];
  }
  return curr;
}

function formDataToEditorText(formData: unknown): string {
  if (typeof formData === 'string') return formData;
  if (!formData) return '';
  return JSON.stringify(formData, null, 2);
}

/**
 * JSON editor that also validates `${placeholder}` usage against known placeholders + user-provided vars.
 *
 * This is designed for Step Function definitions rendered via Terraform `templatefile()`.
 */
export const PlaceholderAwareJson = (props: Props) => {
  const { onChange, schema, uiSchema, formData, formContext } = props;

  const title = schema.title || uiSchema?.['ui:title'] || 'JSON Input';
  const description = schema.description || uiSchema?.['ui:description'] || '';
  const required = Boolean(props.required);

  const varsFieldName = uiSchema?.['ui:options']?.varsFieldName ?? 'definition_template_vars';
  const varsFieldPath = uiSchema?.['ui:options']?.varsFieldPath;
  const enforceTemplatizedDefinition =
    uiSchema?.['ui:options']?.enforceTemplatizedDefinition ?? true;
  const knownPlaceholders = useMemo(
    () => (uiSchema?.['ui:options']?.knownPlaceholders ?? []) as string[],
    [uiSchema],
  );

  const rootFormData = (formContext as any)?.formData as Record<string, unknown> | undefined;
  const templateVarsRaw = getByPath(rootFormData?.[varsFieldName], varsFieldPath);

  const [value, setValue] = useState<string>(() => formDataToEditorText(formData));
  const [jsonErrors, setJsonErrors] = useState<string[]>([]);

  // Keep editor in sync if formData is externally changed (rare, but possible)
  useEffect(() => {
    setValue(formDataToEditorText(formData));
  }, [formData]);

  const handleEditorChange = useCallback(
    (newValue: string | undefined) => {
      if (newValue === undefined) return;
      setValue(newValue);
      // Data change; errors will be computed in an effect and attached via ErrorSchema.
      onChange(newValue);
    },
    [onChange],
  );

  const placeholderInfo = useMemo(() => {
    const text = value ?? '';
    if (!text.trim()) {
      return { valid: [] as string[], invalid: [] as string[] };
    }
    const out = extractPlaceholderTokens(text);
    return { valid: Array.from(new Set(out.valid)), invalid: Array.from(new Set(out.invalid)) };
  }, [value]);

  const templateVarsParsed = useMemo(() => tryParseJsonObject(templateVarsRaw), [templateVarsRaw]);

  const templatizeInfo = useMemo(() => {
    if (!value.trim()) {
      return templatizeStepFunctionDefinition('');
    }
    return templatizeStepFunctionDefinition(value);
  }, [value]);

  const hardcodedErrors = useMemo(() => {
    if (!enforceTemplatizedDefinition) return [] as string[];
    if (!value.trim()) return [] as string[];

    const errs: string[] = [];
    const f = templatizeInfo.hardcodedFindings;

    if (f.regions.length > 0) {
      errs.push(
        `Hardcoded AWS region(s) detected: ${f.regions
          .slice(0, 5)
          .join(', ')}. Please templatize to \${region}.`,
      );
    }
    if (f.accountIds.length > 0) {
      errs.push(
        `Hardcoded AWS account id(s) detected: ${f.accountIds
          .slice(0, 5)
          .join(', ')}. Please templatize to \${account_id}.`,
      );
    }
    if (f.buckets.length > 0) {
      errs.push(
        `Hardcoded bucket name(s) detected: ${f.buckets
          .slice(0, 5)
          .join(', ')}. Please templatize to the appropriate \${..._bucket} placeholder.`,
      );
    }
    if (f.domains.length > 0) {
      errs.push(
        `Hardcoded domain name(s) detected: ${f.domains
          .slice(0, 5)
          .join(
            ', ',
          )}. Please templatize to \${api_domain_name} (and provide it in "${varsFieldName}" if needed).`,
      );
    }

    return errs;
  }, [enforceTemplatizedDefinition, templatizeInfo.hardcodedFindings, value, varsFieldName]);

  const missingPlaceholders = useMemo(() => {
    if (!value.trim()) return [] as string[];
    const available = new Set<string>([
      ...knownPlaceholders,
      ...Object.keys(templateVarsParsed.obj),
    ]);
    return placeholderInfo.valid.filter(p => !available.has(p));
  }, [knownPlaceholders, placeholderInfo.valid, templateVarsParsed.obj, value]);

  const computedErrors = useMemo(() => {
    const errs: string[] = [];
    if (jsonErrors.length > 0) {
      errs.push(...jsonErrors);
    }
    if (value.trim()) {
      if (hardcodedErrors.length > 0) {
        errs.push(...hardcodedErrors);
      }
      if (templateVarsParsed.error) {
        errs.push(
          `Cannot validate placeholders because "${varsFieldName}" ${templateVarsParsed.error}. Fix it to continue.`,
        );
      }
      if (placeholderInfo.invalid.length > 0) {
        errs.push(
          `Invalid placeholder name(s): ${placeholderInfo.invalid.join(
            ', ',
          )}. Use only letters/numbers/underscores inside \${...} (e.g. \${legacy_archived_bucket}).`,
        );
      }
      if (missingPlaceholders.length > 0) {
        errs.push(
          `Missing template variable(s): ${missingPlaceholders.join(
            ', ',
          )}. Add them to "${varsFieldName}" or use an available derived constant.`,
        );
      }
    }
    return errs;
  }, [
    jsonErrors,
    hardcodedErrors,
    missingPlaceholders,
    placeholderInfo.invalid,
    templateVarsParsed.error,
    value,
    varsFieldName,
  ]);

  const lastErrorKeyRef = useRef<string>('');

  // Attach computed errors to the field so the user cannot proceed with missing placeholders.
  useEffect(() => {
    const key = stableErrorKey(computedErrors);
    if (key === lastErrorKeyRef.current) return;
    lastErrorKeyRef.current = key;

    const es: ErrorSchema<string> | undefined =
      computedErrors.length > 0 ? ({ __errors: computedErrors } as any) : undefined;
    // Re-emit current value with updated error schema.
    onChange(value, es as any);
  }, [computedErrors, onChange, value]);

  const hasTemplatizeSuggestions = templatizeInfo.replacements.length > 0;
  const commitValue = useCallback(
    (next: string) => {
      setValue(next);
      onChange(next);
    },
    [onChange],
  );
  const templatize = useTemplatizeToggle(value, templatizeInfo.outputText, commitValue);

  return (
    <Box>
      <FormLabel required={required}>{title}</FormLabel>
      {description && <FormHelperText>{description}</FormHelperText>}

      <TemplatizeSuggestionAlert
        show={value.trim().length > 0 && hasTemplatizeSuggestions}
        replacements={templatizeInfo.replacements}
        onApply={templatize.apply}
        onUndo={templatize.undo}
        canUndo={templatize.canUndo}
      />

      <ValidationErrorsAlert errors={computedErrors} />

      {value.trim() &&
        placeholderInfo.valid.length > 0 &&
        missingPlaceholders.length === 0 &&
        computedErrors.length === 0 && (
          <Alert severity="success" style={{ marginBottom: 8 }}>
            Placeholders detected: {placeholderInfo.valid.join(', ')} (all satisfied)
          </Alert>
        )}

      <JsonMonacoEditor
        value={value}
        onChange={handleEditorChange}
        onSyntaxErrors={setJsonErrors}
      />
    </Box>
  );
};
